import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { QueryBuilderConfig, RuleSet } from 'ngx-angular-query-builder';
import { ApplicationState } from '../../../store/application-store';
import { RouteContextService } from '../../../context/services/route-context.service';
import { WidgetComponent } from '../../../dashboards/components/widget-component/widget-component';
import { IContext } from '../../../context/models/context';
import { BrandService as MojoBrandService } from '../../../brand/services/brand.service';
import * as cashBalanceActions from '../../actions/cash-balances.actions';
import { CashBalancesModel } from '../../models/cash-balances.model';
import * as cashBalanceSelectors from '../../selectors/cash-balance-selectors';
import { CashBalanceChartGroupType } from '../../models/cash-balance-group-type.model';
import { CashBalanceLabelService } from '../../services/cash-balance-label.service';
import * as filterActions from '../../../common/filter/actions/filter.actions';
import { FilterEditorDialogResult } from '../../../common/filter/models/filter-editor-dialog-result';
import { CashBalanceGroupTypeProvider } from '../../services/cash-balance-group-type-provider.service';
import * as filterUtils from '../../../common/filter/filter-utils';
import { DeviceDetectorService } from '../../../common/device-detection/services/device-detector.service';
import { GroupedChart } from '../../../common/models/grouped-chart';
import { GroupedTable } from '../../../common/models/grouped-table';
import { takeWhile } from 'rxjs';
import { CurrencyService } from '../../../currency/services/currency.service';
import { take } from 'rxjs';
import { combineLatest } from 'rxjs';
import { Currency } from '../../../currency/models/currency.model';
import { CashBalanceHttpService } from '../../services/cash-balance-http.service';
import { getEffectiveProduct } from '../../../user-settings/selectors/user-settings';

@Component({
  selector: 'mojo-cash-balance-widget',
  templateUrl: './cash-balances-widget.component.html'
})
export class CashBalanceWidgetComponent extends WidgetComponent implements OnInit, OnDestroy {
  loading$: Observable<boolean>;
  error$: Observable<string>;
  displayDialog: boolean = false;
  allowRuleset: boolean = false;
  context: IContext;
  isAlive: boolean = true;
  cashBalances$: Observable<Array<CashBalancesModel>>;
  cashBalancesForChart$: Observable<GroupedChart>;
  cashBalancesForTable$: Observable<GroupedTable>;
  totalRowForTable$: Observable<object>;
  chartGroupTypes: Array<CashBalanceChartGroupType> = [];
  selectedChartGroupType: CashBalanceChartGroupType;
  currentDate: Date = new Date();
  toolbarDisplayed: boolean = false;
  selectedCurrencyCode: string;
  selectedCurrency: Currency;
  currencyOptions: Currency[];

  queryConfig: QueryBuilderConfig;
  currentFilter: RuleSet;
  chartFontColour: string;
  chartGridLineColour: string;
  chartColours: Array<string>;
  sysAdminFilterText$: Observable<string[]>;
  view$: Observable<string>;
  invalidFields: string[] = [];
  private filterFieldProviderFactoryCallback: cashBalanceSelectors.CashBalancesFilterFieldProviderFactoryCallback;

  constructor(
    private readonly contextService: RouteContextService,
    private readonly activatedRoute: ActivatedRoute,
    protected store: Store<ApplicationState>,
    private readonly mojoBrandService: MojoBrandService,
    private readonly cashBalanceLabelService: CashBalanceLabelService,
    private readonly cashBalanceGroupTypeProvider: CashBalanceGroupTypeProvider,
    protected confirmationService: ConfirmationService,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly currencyService: CurrencyService,
    private readonly cashBalanceService: CashBalanceHttpService
  ) {
    super(store, confirmationService);
    this.chartGroupTypes = this.getChartGroupTypes();
    this.context = this.contextService.getContextFromRoute(this.activatedRoute);
  }

  ngOnInit() {
    super.ngOnInit();

    const currencySetup = combineLatest(
      this.currencyService.getDefaultCurrency(),
      this.currencyService.getCurrencies(),
      this.cashBalanceService.getCashBalanceConfiguration(this.widget.widgetConfigId)
    );

    currencySetup
      .pipe(take(1))
      .subscribe(
        ([defaultCurrency, allCurrencies, cbConfig]) => {

          this.currencyOptions = allCurrencies;
          this.selectedCurrency = defaultCurrency;

          if (cbConfig && cbConfig.Currency) {
            this.selectedCurrency = this.currencyOptions.find(a => a.Code === cbConfig.Currency);
          }

          this.updateGrouping();

        });

    const effectiveProduct = this.store.pipe(
      select(getEffectiveProduct),
      takeWhile(() => this.isAlive)
    );
    effectiveProduct.subscribe((product) => {
      this.filterFieldProviderFactoryCallback = cashBalanceSelectors.makeCashBalancesFilterFieldProviderCallback(product, this.widget.dashboardName);
    });


    this.store.dispatch(new cashBalanceActions.Load(this.context, this.widget.id.toString()));
    this.store.dispatch(new filterActions.LoadWidgetFilter(new filterActions.WidgetFilterActionPayload(this.widget.id)));

    this.store.pipe(
      select(cashBalanceSelectors.getFilter(this.widget.id.toString())),
      takeWhile(() => this.isAlive)
    ).subscribe((filter) => {
      this.currentFilter = filter;
    });

    this.cashBalances$ = this.store.pipe(
      select((state) => state.cashBalances.cashBalances)
    );
    this.store.pipe(
      select(cashBalanceSelectors.getQueryConfig(this.widget.widgetConfigId, this.filterFieldProviderFactoryCallback)),
      takeWhile(() => this.isAlive)
    ).subscribe((config) => {
      this.queryConfig = config;
    });

    this.mojoBrandService
      .getCurrentBrand().pipe(
        takeWhile(() => this.isAlive)
      ).subscribe(brand => {
        this.chartFontColour = brand.chartInfo.ChartFontColour;
        this.chartGridLineColour = brand.chartInfo.ChartGridLineColour;
        this.chartColours = brand.chartInfo.ChartColours;
        this.cashBalancesForChart$ = this.store.pipe(
          select(
            cashBalanceSelectors.getCashBalanceDataForChart(this.widget.id.toString(), this.widget.widgetConfigId, this.chartColours, this.filterFieldProviderFactoryCallback))
        );
      });

    this.cashBalancesForTable$ = this.store.pipe(select(cashBalanceSelectors.getCashBalanceDataForTable(this.widget.id.toString(), this.widget.widgetConfigId, this.filterFieldProviderFactoryCallback)));
    this.totalRowForTable$ = this.store.pipe(select(cashBalanceSelectors.getTotalRow(this.widget.id.toString(), this.widget.widgetConfigId, this.filterFieldProviderFactoryCallback)));
    this.sysAdminFilterText$ = this.store.pipe(select(cashBalanceSelectors.getSysAdminFilterStrings(this.widget.widgetConfigId)));
    this.view$ = this.store.pipe(select(cashBalanceSelectors.getView(this.widget.id.toString())));
    this.store.dispatch(new cashBalanceActions.LoadConfiguration(this.widget.widgetConfigId, this.widget.id.toString()));

    this.store.pipe(
      select(cashBalanceSelectors.getChartGroupType(this.widget.id.toString())),
      takeWhile(() => this.isAlive)
    ).subscribe(
      config => {
        this.selectedChartGroupType = config;
      });
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  get hasCustomFilter(): boolean {
    return filterUtils.isNotDefaultFilter(this.currentFilter);
  }

  getChartGroupTypes(): Array<CashBalanceChartGroupType> {
    const groupTypes: Array<CashBalanceChartGroupType> = this.cashBalanceGroupTypeProvider.getChartGroupTypes();
    this.selectedChartGroupType = groupTypes[0];
    return groupTypes;
  }

  changeToTableView() {
    this.store.dispatch(new cashBalanceActions.UpdateView(this.widget.id.toString(), 'cashBalanceTable'));
  }

  changeToBarView() {
    this.store.dispatch(new cashBalanceActions.UpdateView(this.widget.id.toString(), 'bar'));
  }

  updateGrouping() {
    this.store.dispatch(new cashBalanceActions.UpdateGrouping(this.widget.id.toString(), this.selectedChartGroupType.GroupByField1, this.selectedChartGroupType.GroupByField2, this.selectedCurrency.Code));
  }

  getTitle(): string {
    return 'Balance by ' +
      this.cashBalanceLabelService.getLabelTextForField(this.selectedChartGroupType.GroupByField1) +
      ' then ' +
      this.cashBalanceLabelService.getLabelTextForField(this.selectedChartGroupType.GroupByField2) +
      (this.selectedCurrency ? ' (' + this.selectedCurrency.Symbol + ')' : '');
  }

  filterEditCancelled(evt: Event): void {
  }

  toggleToolbar(): void {
    this.toolbarDisplayed = !this.toolbarDisplayed;
  }

  filterEdited(result: FilterEditorDialogResult): void {
    this.store.dispatch(new filterActions.UpdateWidgetFilter(new filterActions.WidgetFilterActionPayload(this.widget.id, result.filter)));
  }

  isMobile(): boolean {
    return this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
  }
}
